import { useEffect, useRef, useState } from 'react';
import { signOut, useSession } from '../auth/session.ts';
import { ALL_FOLIAGE, FOLIAGE_KINDS, FOLIAGE_LABELS, type FoliageLayer, type LevelDoc } from '../model/format.ts';
import { applyFoliage } from './grass.ts';
import { gameData } from '../data/gameData.ts';
import { deleteLevel, listLevels, loadLevel, type LevelSummary } from '../data/remoteStore.ts';
import { thumbUrl } from '../data/versions.ts';
import { saveNow } from './autosave.ts';
import { terrainStyles } from '../terrain/index.ts';
import { LobbyButton } from '../lobby/ui.tsx';
import { requestPlay } from '../lobby/client.ts';
import { getContainer, openTileset, useEditor, type Tool } from './store.ts';
import { missingSpikeCorners, spikeDests, spikeEditQueue } from './spikes.ts';
import { terrainSurface } from '../model/bake.ts';
import { APP_BUILT, APP_VERSION, hardRefresh, useUpdateAvailable } from './refresh.ts';
import { Check, Icon, Row, Section, Select } from './widgets.tsx';
import { useSettings } from './settings.ts';
import { loadMuted, saveMuted } from '../play/view/audio.ts';
import { prefabLabel } from './catalog.ts';
import { exportDocument, exportForUnity, isLevelDoc, newLevel, readJsonFile } from './levelFiles.ts';

export async function openLevelById(id: string, builtin: boolean) {
  const st = useEditor.getState();
  let doc: LevelDoc | null = null;
  if (!builtin) doc = await loadLevel<LevelDoc>(id);
  if (!doc) {
    const gd = gameData();
    const e = gd.project.levels.find((l) => l.id === id);
    if (e) doc = await (await fetch(`${gd.base}/${e.file}`)).json();
  }
  if (!doc) {
    st.notify('Level not found');
    return;
  }
  st.setDoc(doc, builtin ? null : doc.updatedAt ?? null);
  localStorage.setItem('ple:last', JSON.stringify({ id, builtin }));
  focusLevel(doc);
  st.set({ modal: null });
}

export function focusLevel(doc: LevelDoc) {
  const st = useEditor.getState();
  const spawn = doc.objects.find((o) => o.prefab && gameData().prefabs[o.prefab]?.name === 'PlayerSpawnPoint');
  const p = spawn ? [spawn.x + 8, spawn.y + 3] : [10, 5];
  st.set({ camera: { x: p[0], y: p[1], zoom: Math.max(12, Math.min(40, window.innerWidth / 40)) } });
}

export async function saveCurrent() {
  const st = useEditor.getState();
  if (!st.doc) return;
  const ok = await saveNow();
  const after = useEditor.getState().saveStatus;
  st.notify(after === 'lobby' ? 'The host saves this level' : ok ? 'Saved' : 'Saved on this device (server unavailable)');
}

const TOOLS: { id: Tool; icon: string; name: string; label: string; key: string }[] = [
  { id: 'select', icon: 'select', name: 'Select / move', label: 'Select / move', key: 'V' },
  { id: 'pan', icon: 'pan', name: 'Pan', label: 'Pan', key: 'H' },
  { id: 'polygon', icon: 'polygon', name: 'Terrain polygon', label: 'Terrain polygon', key: 'P' },
  { id: 'tilesToPoly', icon: 'topoly', name: 'Tiles to polygon', label: 'Tiles to polygon: click a tile to convert that connected blob', key: 'U' },
  { id: 'grass', icon: 'grass', name: 'Grass brush', label: 'Grass brush (Alt erases)', key: 'T' },
  { id: 'spikes', icon: 'spikes', name: 'Spike brush', label: 'Spike brush: paint floor, wall or ceiling spikes on surface lines (Alt erases). Slopes are skipped.', key: 'K' },
  { id: 'foliage', icon: 'foliage', name: 'Foliage brush', label: 'Foliage brush: add or remove grass tufts, flowers, vines, trees… (Alt removes)', key: 'N' },
  { id: 'fix', icon: 'fix', name: 'Fix tiles', label: 'Fix terrain tiles: pick the dirt / grass sprite of a cell', key: 'C' },
  { id: 'brush', icon: 'brush', name: 'Tile brush', label: 'Tile brush', key: 'B' },
  { id: 'erase', icon: 'erase', name: 'Erase tiles', label: 'Erase tiles', key: 'E' },
  { id: 'rect', icon: 'rect', name: 'Fill rectangle', label: 'Fill rectangle (Alt erases)', key: 'R' },
  { id: 'eyedropper', icon: 'eyedropper', name: 'Pick tile', label: 'Pick tile', key: 'I' },
  { id: 'object', icon: 'object', name: 'Place object', label: 'Place object', key: 'O' },
  { id: 'waypoint', icon: 'waypoint', name: 'Waypoints', label: 'Waypoints', key: 'W' },
  { id: 'link', icon: 'link', name: 'Link', label: 'Link switches & activators', key: 'L' },
  { id: 'detach', icon: 'detach', name: 'Cut out platform', label: 'Cut tiles out into a moving platform', key: 'D' },
];

export function Toolbar() {
  const tool = useEditor((s) => s.tool);
  const set = useEditor((s) => s.set);
  const pinned = useSettings((s) => s.toolbarPinned);
  const update = useSettings((s) => s.update);
  const [hover, setHover] = useState(false);
  const timer = useRef(0);
  // touch has no hover; a short delay keeps the bar from flashing open when the mouse just crosses it
  const hoverTo = (on: boolean, e: React.PointerEvent) => {
    if (e.pointerType === 'touch') return;
    clearTimeout(timer.current);
    timer.current = window.setTimeout(() => setHover(on), on ? 120 : 200);
  };
  useEffect(() => () => clearTimeout(timer.current), []);
  const open = pinned || hover;
  return (
    <div className={`toolbar-slot ${pinned ? 'pinned' : ''}`}>
      <div className={`toolbar ${open ? 'open' : ''}`} onPointerEnter={(e) => hoverTo(true, e)} onPointerLeave={(e) => hoverTo(false, e)}>
        {TOOLS.map((t) => (
          <button key={t.id} className={`tool ${tool === t.id ? 'active' : ''}`} title={`${t.label} (${t.key})`} onClick={() => set({ tool: t.id, ...(t.id === 'object' ? { panel: 'objects', panelOpen: true } : t.id === 'brush' || t.id === 'rect' ? { panel: 'tiles', panelOpen: true } : {}) })}>
            <Icon name={t.icon} size={22} />
            <span className="tool-label">{t.name}</span>
            <kbd>{t.key}</kbd>
          </button>
        ))}
        <span className="toolbar-gap" />
        <button
          className="tool toolbar-pin"
          title={pinned ? 'Collapse the tool bar (it still expands on hover)' : 'Keep the tool bar expanded'}
          onClick={() => {
            clearTimeout(timer.current);
            setHover(false);
            update({ toolbarPinned: !pinned });
          }}
        >
          <Icon name={pinned ? 'panelclose' : 'panelopen'} size={22} />
          <span className="tool-label">{pinned ? 'Collapse' : 'Keep expanded'}</span>
        </button>
      </div>
    </div>
  );
}

/** Contextual options for the active tool, shown over the viewport. */
export function ToolOptions() {
  const s = useEditor();
  if (s.pickTile) {
    return (
      <div className="tool-options">
        <button className="chip" onClick={() => s.set({ pickTile: null })}><Icon name="close" size={14} /> Cancel</button>
        <span className="hint">Tap a tile in the level to use it as the source. Drag or pinch to move the view.</span>
      </div>
    );
  }
  if (s.pickSpawn) {
    return (
      <div className="tool-options">
        <button className="chip active" onClick={() => requestPlay([s.camera.x, s.camera.y])}><Icon name="target" size={14} /> View centre</button>
        <button className="chip" onClick={() => s.set({ pickSpawn: false })}><Icon name="close" size={14} /> Cancel</button>
        <span className="hint">Tap where the player should start. Drag or pinch to move the view.</span>
      </div>
    );
  }
  if (s.tool === 'polygon') {
    const styles = terrainStyles();
    return (
      <div className="tool-options">
        <Select value={s.polygon.style} options={styles.map((x) => [x.id, x.name] as [string, string])} onChange={(v) => s.set({ polygon: { ...s.polygon, style: v } })} />
        <button className={`chip ${s.polygon.mode === 'add' && !s.polygon.hole ? 'active' : ''}`} onClick={() => s.set({ polygon: { ...s.polygon, mode: 'add', hole: false } })}>
          <Icon name="plus" size={14} /> Add
        </button>
        <button className={`chip ${s.polygon.mode === 'subtract' ? 'active' : ''}`} onClick={() => s.set({ polygon: { ...s.polygon, mode: 'subtract', hole: false } })}>
          <Icon name="minus" size={14} /> Cut
        </button>
        <button className={`chip ${s.polygon.hole ? 'active' : ''}`} title="Draw a hole in the selected shape" onClick={() => s.set({ polygon: { ...s.polygon, hole: !s.polygon.hole, mode: 'add' } })}>
          <Icon name="hole" size={14} /> Hole
        </button>
        <button className="chip" onClick={() => window.dispatchEvent(new Event('ple:polygon-finish'))}><Icon name="check" size={14} /> Close</button>
        <button className="chip" onClick={() => window.dispatchEvent(new Event('ple:polygon-cancel'))}><Icon name="close" size={14} /></button>
        <span className="hint">Tap points; edges snap to straight lines and 1:1, 2:1 or 1:2 slopes. Tap the first point or Enter to close.</span>
      </div>
    );
  }
  if (s.tool === 'tilesToPoly') {
    const styles = terrainStyles();
    return (
      <div className="tool-options">
        <Select value={s.polygon.style} options={styles.map((x) => [x.id, x.name] as [string, string])} onChange={(v) => s.set({ polygon: { ...s.polygon, style: v } })} />
        <span className="hint">Click a tile. Every connected tile becomes one terrain polygon. All-grass blobs keep Full grass; mixed blobs get None, with the grass that was already there painted on.</span>
      </div>
    );
  }
  if (s.tool === 'grass') {
    const g = s.grass;
    const set = (p: Partial<typeof g>) => s.set({ grass: { ...g, ...p } });
    const cont = s.doc ? (s.container === 'level' ? s.doc : s.doc.bodies.find((b) => b.id === s.container)) : undefined;
    const painted = !!(cont?.grassLines?.length || cont?.grassPaint?.length);
    return (
      <div className="tool-options">
        <button className={`chip ${g.mode === 'paint' ? 'active' : ''}`} onClick={() => set({ mode: 'paint' })}><Icon name="grass" size={14} /> Paint</button>
        <button className={`chip ${g.mode === 'erase' ? 'active' : ''}`} onClick={() => set({ mode: 'erase' })}><Icon name="erase" size={14} /> Erase</button>
        <button className={`chip ${g.mode === 'restore' ? 'active' : ''}`} title="Back to the shape's own grass setting (Auto / Full / None)" onClick={() => set({ mode: 'restore' })}><Icon name="undo" size={14} /> Auto</button>
        <span className="sep" />
        <button className={`chip ${!g.rect ? 'active' : ''}`} onClick={() => set({ rect: false })}><Icon name="brush" size={14} /> Brush</button>
        <button className={`chip ${g.rect ? 'active' : ''}`} onClick={() => set({ rect: true })}><Icon name="rect" size={14} /> Rect</button>
        {!g.rect && (
          <button className={`chip ${g.join ? 'active' : ''}`} title="Every click marks all the lines from the last marked one to it (the same as Shift-click)" onClick={() => set({ join: !g.join })}>
            Fill between
          </button>
        )}
        <button
          className="chip"
          disabled={!painted}
          title="Remove all grass marks in this level (or body)"
          onClick={() =>
            s.change('Clear grass', (d) => {
              const c = s.container === 'level' ? d : d.bodies.find((b) => b.id === s.container);
              if (!c) return;
              delete c.grassLines;
              delete c.grassPaint;
            })
          }
        >
          <Icon name="trash" size={14} /> Clear
        </button>
        <span className="hint">
          {window.matchMedia('(pointer: coarse)').matches
            ? 'Tap a surface line to mark it. With Fill between on, each tap marks every line from the last one. Drag along a surface to mark as you go.'
            : 'Click a surface line to mark it. Shift-click another line to mark every line between. Drag along a surface to mark as you go.'}
        </span>
      </div>
    );
  }
  if (s.tool === 'spikes') {
    const g = s.spikes;
    const set = (p: Partial<typeof g>) => s.set({ spikes: { ...g, ...p } });
    const cont = s.doc ? getContainer(s.doc, s.container) : undefined;
    const painted = !!cont?.spikeLines?.length;
    const dests = s.doc && cont ? spikeDests(cont, terrainSurface(s.doc, cont)) : [];
    const missing = missingSpikeCorners(dests);
    const corners = dests.filter((d) => d.corner);
    return (
      <div className="tool-options">
        <button className={`chip ${g.mode === 'paint' ? 'active' : ''}`} onClick={() => set({ mode: 'paint' })}><Icon name="spikes" size={14} /> Paint</button>
        <button className={`chip ${g.mode === 'erase' ? 'active' : ''}`} onClick={() => set({ mode: 'erase' })}><Icon name="erase" size={14} /> Erase</button>
        <span className="sep" />
        <button className={`chip ${!g.rect ? 'active' : ''}`} onClick={() => set({ rect: false })}><Icon name="brush" size={14} /> Brush</button>
        <button className={`chip ${g.rect ? 'active' : ''}`} onClick={() => set({ rect: true })}><Icon name="rect" size={14} /> Rect</button>
        {!g.rect && (
          <button className={`chip ${g.join ? 'active' : ''}`} title="Every click marks all the lines from the last marked one to it (the same as Shift-click)" onClick={() => set({ join: !g.join })}>
            Fill between
          </button>
        )}
        <button
          className="chip"
          disabled={!corners.length}
          title={missing.length ? `${missing.length} inside corner${missing.length === 1 ? '' : 's'} need a tile` : 'Draw or edit an inside-corner spike tile'}
          onClick={() => {
            if (!s.doc) return;
            const q = spikeEditQueue(s.container, dests);
            if (q) s.set({ tileEdit: q });
            else s.notify('Paint both sides of an inside corner, then draw its tile');
          }}
        >
          <Icon name="pencil" size={14} /> {missing.length ? `Draw corners (${missing.length})` : 'Draw corners'}
        </button>
        <button
          className="chip"
          disabled={!painted}
          title="Remove all spike marks in this level (or body)"
          onClick={() =>
            s.change('Clear spikes', (d) => {
              const c = s.container === 'level' ? d : d.bodies.find((b) => b.id === s.container);
              if (!c) return;
              delete c.spikeLines;
            })
          }
        >
          <Icon name="trash" size={14} /> Clear
        </button>
        <span className="hint">
          {window.matchMedia('(pointer: coarse)').matches
            ? 'Tap a floor, wall or ceiling line to put spikes on that side. Inside corners: paint one side, the other, or both. Double-tap a pink corner to draw its tile. Slopes cannot take spikes.'
            : 'Click a floor, wall or ceiling line to put spikes on that side. Inside corners: paint one side, the other, or both. Double-click a pink corner to draw its tile. Slopes cannot take spikes.'}
        </span>
      </div>
    );
  }
  if (s.tool === 'foliage') {
    const f = s.foliage;
    const set = (p: Partial<typeof f>) => s.set({ foliage: { ...f, ...p } });
    const painted = s.doc ? (s.container === 'level' ? s.doc.decoPaint : s.doc.bodies.find((b) => b.id === s.container)?.decoPaint) : undefined;
    const kind = f.kind === 'any' ? 'foliage' : FOLIAGE_LABELS[f.kind].toLowerCase();
    const where = f.layer === 'both' ? '' : f.layer === 'front' ? 'foreground ' : 'background ';
    const what = where + kind;
    const layers: FoliageLayer[] = f.layer === 'both' ? ['front', 'back'] : [f.layer];
    return (
      <div className="tool-options">
        <button className={`chip ${f.kind === 'any' ? 'active' : ''}`} title="Every kind of foliage (not terrain details)" onClick={() => set({ kind: 'any' })}><Icon name="foliage" size={14} /> Any</button>
        {FOLIAGE_KINDS.map((k) => (
          <button key={k} className={`chip ${f.kind === k ? 'active' : ''}`} onClick={() => set({ kind: k })}><Icon name={k === 'grass' ? 'grass' : k} size={14} /> {FOLIAGE_LABELS[k]}</button>
        ))}
        <span className="sep" />
        <button className={`chip ${f.layer === 'both' ? 'active' : ''}`} title="Foreground and background decorations" onClick={() => set({ layer: 'both' })}>Fg + Bg</button>
        <button className={`chip ${f.layer === 'front' ? 'active' : ''}`} title="Decorations in front of the player (foreground layer, and terrain details on the level layer)" onClick={() => set({ layer: 'front' })}>Foreground</button>
        <button className={`chip ${f.layer === 'back' ? 'active' : ''}`} title="Decorations behind the player (background layer)" onClick={() => set({ layer: 'back' })}>Background</button>
        <span className="sep" />
        <button className={`chip ${f.mode === 'add' ? 'active' : ''}`} onClick={() => set({ mode: 'add' })}><Icon name="plus" size={14} /> Add</button>
        <button className={`chip ${f.mode === 'remove' ? 'active' : ''}`} onClick={() => set({ mode: 'remove' })}><Icon name="minus" size={14} /> Remove</button>
        <button className={`chip ${f.mode === 'restore' ? 'active' : ''}`} title="Back to the shape's own decoration density" onClick={() => set({ mode: 'restore' })}><Icon name="undo" size={14} /> Auto</button>
        <span className="sep" />
        <button className={`chip ${!f.rect ? 'active' : ''}`} onClick={() => set({ rect: false })}><Icon name="brush" size={14} /> Brush</button>
        <button className={`chip ${f.rect ? 'active' : ''}`} onClick={() => set({ rect: true })}><Icon name="rect" size={14} /> Rect</button>
        {!f.rect && <Select value={f.size} options={[1, 2, 3, 5, 8].map((n) => [n, `${n}×${n}`] as [number, string])} onChange={(v) => set({ size: v })} />}
        <button
          className="chip"
          disabled={!painted?.length}
          title={`Remove all added and removed ${where}foliage in this level (or body)`}
          onClick={() =>
            s.change(`Clear painted ${where}foliage`, (d) => {
              const c = s.container === 'level' ? d : d.bodies.find((b) => b.id === s.container);
              if (!c?.decoPaint) return;
              const cells: [number, number][] = [];
              for (let i = 0; i + 2 < c.decoPaint.length; i += 3) cells.push([c.decoPaint[i], c.decoPaint[i + 1]]);
              const next = applyFoliage(c.decoPaint, cells, 'restore', ALL_FOLIAGE, layers);
              if (next) c.decoPaint = next;
              else delete c.decoPaint;
            })
          }
        >
          <Icon name="trash" size={14} /> Clear
        </button>
        <span className="hint">
          {f.mode === 'add' ? `Paint where ${what} should grow: every spot it fits gets one.` : f.mode === 'remove' ? `Paint where no ${what} may grow.` : `Paint to undo added / removed ${what}.`} Alt removes.
        </span>
      </div>
    );
  }
  if (s.tool === 'detach') return <div className="tool-options"><span className="hint">Drag over level tiles to cut them out into a new moving platform. To move a whole shape, right-click it → Make moving platform.</span></div>;
  if (s.tool === 'link') {
    if (s.linkSlot) {
      return (
        <div className="tool-options">
          <button className="chip active" onClick={() => s.set({ tool: 'select' })}><Icon name="check" size={14} /> Done</button>
          <span className="hint">Picking <b>{s.linkSlot.label}</b>: tap {s.linkSlot.accept === 'panel' ? 'control panels' : s.linkSlot.accept === 'body' ? 'platforms or blocks' : 'switches, zones, timers…'} to link or unlink them.</span>
        </div>
      );
    }
    return <div className="tool-options"><span className="hint">Tap a platform, laser, cannon, gate… then tap the switches / zones that drive it. With a switch selected, tapping a platform links the switch to it.</span></div>;
  }
  if (s.tool === 'waypoint') return <div className="tool-options"><span className="hint">Select a moving platform, then tap to add waypoints; drag the numbered handles to move them.</span></div>;
  if (s.tool === 'object') {
    const name = s.objectPrefab ? prefabLabel(s.objectPrefab) : null;
    return (
      <div className="tool-options">
        <button className={`chip ${s.objectPlace === 'paint' ? 'active' : ''}`} onClick={() => s.set({ objectPlace: 'paint' })}>
          <Icon name="brush" size={14} /> Paint
        </button>
        <button className={`chip ${s.objectPlace === 'rect' ? 'active' : ''}`} onClick={() => s.set({ objectPlace: 'rect' })}>
          <Icon name="rect" size={14} /> Fill
        </button>
        <span className="hint">
          {name
            ? s.objectPlace === 'rect'
              ? `Drag a box to place ${name} in every grid square. One per cell.`
              : `Drag to place ${name}, one per grid square. Space-drag pans.`
            : 'Pick an object in the Objects panel.'}
        </span>
      </div>
    );
  }
  return null;
}

function SaveBadge() {
  const status = useEditor((s) => s.saveStatus);
  const savedAt = useEditor((s) => s.savedAt);
  const label = { saved: 'Saved', pending: 'Unsaved', saving: 'Saving…', offline: 'Saved on this device', denied: 'Not saved', lobby: 'Host saves' }[status];
  const title =
    status === 'offline'
      ? 'The server could not be reached. Your changes are kept in this browser and upload on the next save.'
      : status === 'denied'
        ? 'The server rejected this save. Your changes are still in this browser.'
      : status === 'lobby'
        ? 'You are a guest in this lobby. The host saves the level.'
      : status === 'saved'
        ? `Changes save automatically${savedAt ? ` · last saved ${new Date(savedAt).toLocaleTimeString()}` : ''}`
        : 'Changes save automatically a moment after you stop editing';
  return <span className={`save-badge ${status}`} title={title}>{label}</span>;
}

export function TopBar() {
  const s = useEditor();
  const doc = s.doc;
  const session = useSession();
  const [menu, setMenu] = useState(false);
  const update = useUpdateAvailable();
  return (
    <div className="topbar">
      <button className="icon-btn" title="Levels" onClick={() => s.set({ modal: 'open' })}><Icon name="folder" /></button>
      <div className="title">
        <span className="level-name">{doc?.name ?? 'No level'}</span>
        {doc && <SaveBadge />}
        {s.container !== 'level' && <span className="badge">editing body</span>}
      </div>
      <div className="spacer" />
      <button className="icon-btn" title="Undo (Ctrl+Z)" disabled={!s.past.length} onClick={s.undo}><Icon name="undo" /></button>
      <button className="icon-btn" title="Redo (Ctrl+Shift+Z)" disabled={!s.future.length} onClick={s.redo}><Icon name="redo" /></button>
      <button className={`icon-btn wide-only ${s.showGrid ? 'on' : ''}`} title="Grid (G)" onClick={() => s.set({ showGrid: !s.showGrid })}><Icon name="grid" /></button>
      <button className="icon-btn" title="Save (Ctrl+S). Changes also save automatically." onClick={saveCurrent}><Icon name="save" /></button>
      <button className="icon-btn wide-only" title="Version history" disabled={!doc} onClick={() => s.set({ modal: 'history' })}><Icon name="history" /></button>
      <button className="icon-btn wide-only text-btn" title="Tileset tool: tile cases (every slope combination), edge rules, fill, decorations…" onClick={() => openTileset()}><Icon name="cases" /> <span>Tile cases</span></button>
      <LobbyButton />
      {session?.isAdmin && <button className="icon-btn wide-only" title="People who can use the editor" onClick={() => s.set({ modal: 'access' })}><Icon name="people" /></button>}
      <button className="icon-btn wide-only" title="Sound profiles: pick the sound for every game action" onClick={() => s.set({ modal: 'sounds' })}><Icon name="sound" /></button>
      <button className="icon-btn wide-only" title="Settings" onClick={() => s.set({ modal: 'settings' })}><Icon name="settings" /></button>
      <div className="play-split">
        <button className="play-btn" title="Play" onClick={() => requestPlay()}><Icon name="play" size={18} /> <span>Play</span></button>
        <button className={`play-btn play-here ${s.pickSpawn ? 'on' : ''}`} title="Play from here: tap a spot in the level (or long-press / right-click anywhere)" onClick={() => s.set({ pickSpawn: !s.pickSpawn })}>
          <Icon name="playhere" size={18} />
        </button>
      </div>
      <div className="menu-wrap">
        <button className="icon-btn" onClick={() => setMenu(!menu)}><Icon name="menu" /></button>
        {menu && (
          <div className="menu" onClick={() => setMenu(false)}>
            <button onClick={() => requestPlay([s.camera.x, s.camera.y])}><Icon name="play" size={16} /> Play from view centre</button>
            <button onClick={() => s.set({ modal: 'lobby' })}><Icon name="people" size={16} /> Lobby</button>
            <button onClick={() => doc && exportForUnity(doc)}><Icon name="download" size={16} /> Export for Unity (baked JSON)</button>
            <button onClick={() => doc && exportDocument(doc)}><Icon name="download" size={16} /> Download level document</button>
            <button className="narrow-only" onClick={() => s.set({ showGrid: !s.showGrid })}><Icon name="grid" size={16} /> {s.showGrid ? 'Hide' : 'Show'} grid</button>
            <button onClick={() => openTileset()}><Icon name="cases" size={16} /> Tile cases (Tileset tool)</button>
            <button onClick={() => s.set({ showLinks: !s.showLinks })}><Icon name="link" size={16} /> {s.showLinks ? 'Hide' : 'Show'} links & paths</button>
            <button onClick={() => s.set({ modal: 'history' })} disabled={!doc}><Icon name="history" size={16} /> Version history</button>
            <button onClick={() => s.set({ modal: 'sounds' })}><Icon name="sound" size={16} /> Sound profiles</button>
            <button onClick={() => s.set({ modal: 'settings' })}><Icon name="settings" size={16} /> Settings</button>
            {session?.isAdmin && <button onClick={() => s.set({ modal: 'access' })}><Icon name="people" size={16} /> People</button>}
            <button onClick={() => s.set({ modal: 'help' })}><Icon name="help" size={16} /> Help & shortcuts</button>
            {session && <div className="menu-user">{session.email ?? 'Signed in'}</div>}
            <button onClick={() => void signOut()}><Icon name="exit" size={16} /> Sign out</button>
          </div>
        )}
      </div>
      <button
        className={`icon-btn refresh-btn ${update ? 'update' : ''}`}
        title={`${update ? 'A new version is available. ' : ''}Hard refresh: download the latest version (running ${APP_VERSION}, built ${new Date(APP_BUILT).toLocaleString()})`}
        onClick={() => hardRefresh(saveCurrent)}
      >
        <Icon name="refresh" />
      </button>
    </div>
  );
}

export function OpenDialog() {
  const s = useEditor();
  const gd = gameData();
  const [saved, setSaved] = useState<LevelSummary[] | null>(null);
  const [name, setName] = useState('New level');
  const [style, setStyle] = useState(terrainStyles()[0]?.id ?? '');
  useEffect(() => {
    listLevels().then(setSaved).catch((e: unknown) => {
      setSaved([]);
      useEditor.getState().notify(e instanceof Error ? e.message : 'Could not load levels');
    });
  }, []);
  const create = async () => {
    const template = gd.project.levels[0] ? await (await fetch(`${gd.base}/${gd.project.levels.find((l) => l.id === 'medium')?.file ?? gd.project.levels[0].file}`)).json() : null;
    const doc = newLevel(name, template, style);
    s.setDoc(doc, null);
    s.set({ modal: null, dirty: true });
    focusLevel(doc);
    await saveNow();
  };
  const importFile = async () => {
    try {
      const d = await readJsonFile();
      if (!isLevelDoc(d)) throw new Error('Not a level document');
      s.setDoc(d, null);
      s.set({ modal: null, dirty: true });
      focusLevel(d);
    } catch (e) {
      s.notify((e as Error).message);
    }
  };
  return (
    <div className="modal-back" onClick={() => s.doc && s.set({ modal: null })}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <h2>Levels</h2>
          {s.doc && <button className="icon-btn" onClick={() => s.set({ modal: null })}><Icon name="close" /></button>}
        </div>
        <div className="modal-body">
          <h3>New level</h3>
          <div className="inline wrap">
            <input className="text" value={name} onChange={(e) => setName(e.target.value)} />
            <Select value={style} options={terrainStyles().map((x) => [x.id, x.name] as [string, string])} onChange={setStyle} />
            <button className="btn primary" onClick={create}>Create</button>
            <button className="btn" onClick={importFile}><Icon name="upload" size={14} /> Import JSON</button>
          </div>
          <h3>Saved levels</h3>
          {saved === null && <div className="muted">Loading…</div>}
          {saved?.length === 0 && <div className="muted">Nothing saved yet.</div>}
          <div className="level-list">
            {saved?.map((l) => (
              <div key={l.id} className="level-item">
                <button className="level-open" onClick={() => openLevelById(l.id, false)}>
                  <span className="level-thumb">{l.thumbVersion && <img src={thumbUrl(l.id, l.thumbVersion)} alt="" loading="lazy" />}</span>
                  <span className="level-text">
                    <b>{l.name}</b>
                    <span className="muted small">{l.ownerEmail ? `${l.ownerEmail} · ` : ''}{l.updatedAt ? new Date(l.updatedAt).toLocaleString() : ''}</span>
                  </span>
                </button>
                <button
                  className="icon-btn"
                  title="Delete"
                  onClick={async () => {
                    if (!confirm(`Delete ${l.name} and its version history?`)) return;
                    await deleteLevel(l.id);
                    setSaved(await listLevels());
                  }}
                >
                  <Icon name="trash" size={16} />
                </button>
              </div>
            ))}
          </div>
          <h3>Levels imported from Unity</h3>
          <div className="level-list">
            {gd.project.levels.map((l) => (
              <div key={l.id} className="level-item">
                <button className="level-open" onClick={() => openLevelById(l.id, true)}>
                  <span className="level-text">
                    <b>{l.name}</b>
                    <span className="muted small">{saved?.some((x) => x.id === l.id) ? 'original · you have an edited copy in Saved levels' : 'original'}</span>
                  </span>
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export function SettingsDialog() {
  const s = useEditor();
  const cfg = useSettings();
  const [muted, setMuted] = useState(loadMuted);
  const close = () => s.set({ modal: null });
  return (
    <div className="modal-back" onClick={close}>
      <div className="modal settings" onClick={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <h2>Settings</h2>
          <button className="icon-btn" onClick={close}><Icon name="close" /></button>
        </div>
        <div className="modal-body">
          <Section title="Editing">
            <Row label="Snap objects to">
              <Select<number> value={cfg.snap} options={[[1, '1 tile'], [0.5, '½ tile'], [0.25, '¼ tile'], [0.125, '⅛ tile'], [0, 'No snapping']]} onChange={(v) => cfg.update({ snap: v })} />
            </Row>
            <Row label="Take objects along" title="Make moving platform also moves the coins, spikes… standing on or inside the shape">
              <Check value={cfg.convertTakesObjects} onChange={(v) => cfg.update({ convertTakesObjects: v })} />
            </Row>
          </Section>
          <Section title="View">
            <Row label="Grid"><Check value={s.showGrid} onChange={(v) => s.set({ showGrid: v })} /></Row>
            <Row label="Links & paths" title="Draw switch links and platform paths for everything, not only the selection">
              <Check value={s.showLinks} onChange={(v) => s.set({ showLinks: v })} />
            </Row>
            <Row label="Mark missing tiles" title="Pink cells: terrain on a slope its tileset has no tile for yet (pick one with the Fix tool or draw it)">
              <Check value={cfg.showMissing} onChange={(v) => cfg.update({ showMissing: v })} />
            </Row>
            <Row label="Camera bounds" title="Shade the area outside the level's camera bounds (edit them in the Level panel)">
              <Check value={cfg.showCameraBounds} onChange={(v) => cfg.update({ showCameraBounds: v })} />
            </Row>
          </Section>
          <Section title="Play">
            <Row label="Sound"><Check value={!muted} onChange={(v) => { saveMuted(!v); setMuted(!v); }} /></Row>
          </Section>
          <Section title="Advanced">
            <Row label="Show raw Unity fields" title="Every serialized component field, folders, sorting layers and output layers in the inspector">
              <Check value={cfg.advanced} onChange={(v) => cfg.update({ advanced: v })} />
            </Row>
            <Row label="Show internal objects" title="List helper prefabs (folders, colliders, parallax parts…) in the Objects panel">
              <Check value={cfg.allPrefabs} onChange={(v) => cfg.update({ allPrefabs: v })} />
            </Row>
          </Section>
          <div className="insp-actions">
            <button className="btn" onClick={() => { cfg.reset(); const d = useSettings.getState(); s.set({ showGrid: d.showGrid, showLinks: d.showLinks }); }}>Reset to defaults</button>
          </div>
        </div>
      </div>
    </div>
  );
}

export function HelpDialog() {
  const set = useEditor((s) => s.set);
  return (
    <div className="modal-back" onClick={() => set({ modal: null })}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <h2>Help</h2>
          <button className="icon-btn" onClick={() => set({ modal: null })}><Icon name="close" /></button>
        </div>
        <div className="modal-body help">
          <h3>Navigation</h3>
          <p>Mouse wheel / pinch: zoom. Middle or right drag, Space+drag, two-finger drag, a stylus (S Pen) with its side button held, or the Pan tool: pan. Pressing the side button mid-stroke ends the stroke and pans; letting it go with the pen still down carries on painting with brush, erase, grass, spikes and foliage. F: focus selection. On touch, a one-finger drag also pans with tools that only place things on a tap (polygon points, pick, link). The object tool paints one per grid square as you drag; Space-drag pans. Long-press (touch) or right-click opens a menu with Play from here, Edit inside (on a moving platform or an object that has one: edit its shape, tiles and objects), Done editing (while inside one), Make moving platform (on a level shape) and Move back to level (on a platform).</p>
          <h3>Tools</h3>
          <p>Hover the tool bar to see each tool's name and shortcut; the button at its bottom keeps it expanded.</p>
          <ul>
            <li><b>V Select</b>: tap objects, bodies or terrain shapes; drag to move (snaps to ½ tile, Shift = free). Drag empty space to box-select. Drag waypoint handles and terrain vertices. Click an edge of the selected shape to add a point there and drag it into place; drop a point onto its neighbour to remove it.</li>
            <li><b>P Polygon</b>: draw terrain. Edges snap to straight, 45°, 2:1 and 1:2 with every tileset. Cells on a slope the tileset has no tile for are marked pink (missing tiles; Settings → Mark missing tiles): pick one with the Fix tool or draw it. Add / Cut / Hole modes. Each shape outputs ground, foreground and background layers with auto shading and decoration (see the inspector).</li>
            <li><b>T Grass</b>: paint grass (or the style's edge art) onto terrain surfaces, erase it, or restore the shape's own setting, with a brush or a rectangle. Only surface cells take paint, and the ends of each run get the tapered pieces. Alt erases. <b>Lines</b> marks single lines instead of whole cells (each side of a cell and its slope), e.g. grass on top of a corner but not down its side: click a line, Shift-click (or Fill between) another to mark every line between, or drag along the surface. Surface decorations stay off lines marked bare.</li>
            <li><b>K Spikes</b>: paint spikes onto terrain outline lines the same way. The brush picks floor, wall or ceiling tiles from the shape's tileset. Inside corners can have one side, the other, or both (both shares one cell: draw a corner tile). Angled slopes cannot take spikes. Double-click a pink dest cell (or Draw corners) to open the pixel editor.</li>
            <li><b>N Foliage</b>: add or remove decorations by painting: any kind, or only grass tufts, flowers, vines, trees, rocks or props. Added cells get a decoration wherever one fits (even with decoration unticked for the shape, but not when its master or that kind's slider is Off); removed cells keep that kind out. Fg + Bg / Foreground / Background picks which decorations it works on (foreground includes terrain details). Auto goes back to the shape's density sliders (Inspector → Decoration). Alt removes.</li>
            <li><b>C Fix tiles</b>: tap a terrain cell to see its dirt (level layer) and grass (foreground) tile and pick the right sprite. The choice is linked to the cell's tile case, so every cell of that style with the same neighbourhood (and its mirror image) changes too. In a lobby those case links and new custom tiles are sent to everyone. <b>Tile cases</b> (top bar, ☰ menu on small screens, or All cases in the Fix panel) lists every case, including every slope combination, with drawing templates for the missing ones.</li>
            <li><b>U Tiles to polygon</b>: click a painted tile. Every connected tile (ground, one-way, spikes, foreground) becomes one terrain polygon; holes stay holes. If the blob is all grass, the shape’s Grass / edge is Full. Otherwise it is None, and the grass tiles that were already there are painted onto the shape. Pick the tileset in the tool bar.</li>
            <li><b>B Brush, E Erase, R Rectangle, I Pick</b>: raw tile editing on the active layer. X / Y flip and Z rotate the brush. Erasing a generated terrain tile masks it.</li>
            <li><b>O Object</b>: place objects from the Objects panel. Paint drags one object per grid square (coins, spikes…); Fill drags a box and puts one in every cell. Space-drag pans. The inspector shows the settings that matter for each object (times in seconds, pick buttons for links); Settings → Show raw Unity fields reveals everything.</li>
            <li><b>W Waypoints</b>: edit a moving platform's path.</li>
            <li><b>L Link</b>: tap a platform, laser, cannon, logic gate, fade block… then tap the switches / zones / timers that drive it. Each “Pick” button in the inspector links into that exact field (e.g. a dynamic object's kill and respawn triggers, a controller's panels and bodies).</li>
            <li><b>D Detach</b>: drag over level tiles to cut them out into a new moving platform.</li>
          </ul>
          <h3>Moving platforms & blocks</h3>
          <ul>
            <li>Select a level shape and use <b>Make it move</b> (or right-click it) to turn it into a moving platform, fading block, rotating block, player-controlled block or static block. Its tiles, grass and the objects standing on it come along.</li>
            <li>Change the type any time in the inspector; <b>Move back to level</b> undoes it. Use “Edit tiles & terrain” to edit its contents.</li>
          </ul>
          <h3>Keys</h3>
          <p>Ctrl+Z / Ctrl+Shift+Z undo/redo · Ctrl+S save · Ctrl+D duplicate · Delete remove · Arrows nudge · G grid · Tab collapse/expand the side panel (drag its left edge to resize it, double-click the edge for the default width) · Esc deselect · Enter close polygon · Backspace remove last point.</p>
          <h3>Ideas &amp; bugs</h3>
          <p>The lightbulb at the bottom right opens a shared list. Post an idea or a bug; everyone with editor access can read and edit. Items are numbered so you can tell an agent “Fix idea 3”. After an agent ships a fix it marks that number solved with a short writeup via <code>POST /api/ideas/solve</code>. Agents read the list from <code>GET /api/ideas</code> (plain text at <code>GET /api/ideas.txt</code>) with <code>Authorization: Bearer $IDEAS_AGENT_TOKEN</code>.</p>
          <h3>Play</h3>
          <p>Play runs the level with Box2D physics at the game's 120 Hz step. Esc returns to the editor. Play from here: press the target button next to Play and tap a spot, or long-press / right-click the level. The player starts standing at that spot (lifted out of the ground if needed) and restarts there; checkpoints still work as usual.</p>
          <ul>
            <li><b>Player 1 (keyboard)</b>: WASD move · Space/W jump · Shift/F or click dash · E/Enter use · Q powerup · R/Backspace last checkpoint · P pause · IJKL pan camera / control panels · Tab camera. Esc still leaves play. Hold Ctrl when you pause for the debug menu (powerups, speed, cut the string).</li>
            <li><b>Fly</b>: hold Ctrl and IJKL (or LB and the right stick — not Y). Right trigger goes faster. Ctrl + − / = saves a spot and warps back. Ctrl + Tab also drops you onto the other player. − / = change hue. Ctrl + ] / Tab change lightness. Z or the left trigger plus Tab saves a checkpoint; plus − restores it. Gamepad d-pad left/right changes hue, and up/down while LB is held changes lightness.</li>
            <li><b>Player 2 (keyboard)</b>: press an arrow key to join. Arrows move · ↑ or / jump · . or Right Shift dash · , use. In a lobby this takes the next free player slot for you.</li>
            <li><b>Gamepads</b>: press any button to join (up to 4 players in the level, and each person can join more than one). Stick move · A jump · X or Y dash · B use · RB powerup · Start pause · Select last checkpoint. Z or the left trigger plus d-pad up joins another player on that same device. Use plus d-pad up switches which of your players that device controls. Hold LB when you pause for the debug menu.</li>
            <li><b>Touch</b>: on-screen stick with jump and dash buttons.</li>
          </ul>
          <h3>Unity</h3>
          <p>Menu → Export for Unity downloads a baked JSON. In Unity use Tools → Level Editor → Import Level JSON to generate a scene.</p>
        </div>
      </div>
    </div>
  );
}
